import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import Header from '../../components/Header';
import { api } from '../../helpers/apiUtils';

/**
 * external_sam_clients + external_sam_footer_data CRUD UI.
 *
 * Lower priority than the scheduling view — these tables are edited rarely
 * (4 clients, footer is static legal copy). Kept simple: pick a client, edit
 * fields, save.
 */

interface ClientRow {
  client_code: string;
  client_name: string;
  active: number;
  external_sam_enabled: number;
  allowed_customers: string;
  reply_to_email: string;
  mailchimp_client: string | null;
  coreg_export_configuration_id: number | null;
  sam_cooldown_days: number;
  product_cooldown_days: number;
  // Layer-2 cooldown override (2026-05-23): when SAM score >= 60, this
  // cooldown overrides (sam_cooldown_days × 2) in Layer 2 of selection.
  // Lets star SAMs repeat faster than the bulk pool.
  high_score_sam_cooldown_days: number;
  new_coreg_leads_tag_id: number | null;
  new_coreg_expiry_days: number | null;
  werbe_sperre_tag_id: number | null;
  werbe_sperre_expiry_days: number | null;
  gev_duplicates_tag_id: number | null;
  i_duplicates_tag_id: number | null;
  excluded_tag_ids: string | null;
  url_replacement_from: string | null;
  url_replacement_to: string | null;
  url_tracking_parameter: string | null;
  use_custom_footer: number;
}

interface FooterRow {
  client_code: string;
  company_name: string;
  street_address: string;
  postal_code: string;
  city: string;
  country: string | null;
  vat_id: string | null;
  phone: string | null;
  email: string | null;
  website_url: string;
  privacy_url: string;
  disclaimer_text: string | null;
  disclaimer_url: string | null;
  active: number;
}

const CLIENT_FIELD_GROUPS: Array<{ label: string; fields: Array<keyof ClientRow> }> = [
  {
    label: 'Allgemein',
    fields: [
      'client_name',
      'active',
      'external_sam_enabled',
      'reply_to_email',
      'mailchimp_client',
      'coreg_export_configuration_id',
      'allowed_customers',
    ],
  },
  {
    label: 'Cooldowns',
    fields: [
      'sam_cooldown_days',
      'product_cooldown_days',
      'high_score_sam_cooldown_days',
      'new_coreg_expiry_days',
      'werbe_sperre_expiry_days',
    ],
  },
  {
    label: 'Tag-IDs',
    fields: [
      'new_coreg_leads_tag_id',
      'werbe_sperre_tag_id',
      'gev_duplicates_tag_id',
      'i_duplicates_tag_id',
      'excluded_tag_ids',
    ],
  },
  {
    label: 'URL-Tracking',
    fields: ['url_replacement_from', 'url_replacement_to', 'url_tracking_parameter'],
  },
  {
    label: 'Footer',
    fields: ['use_custom_footer'],
  },
];

/** Defaults for the "new client" dialog draft. */
const NEW_CLIENT_DEFAULTS: Record<string, string> = {
  client_code: '',
  client_name: '',
  reply_to_email: '',
  mailchimp_client: '',
  allowed_customers: '[]',
  active: '1',
  external_sam_enabled: '0',
  sam_cooldown_days: '14',
  product_cooldown_days: '8',
};

/** Text fields shown in the "new client" dialog. The rest (tag IDs, URL
 * tracking, footer) are edited via the normal edit form after creation. */
const NEW_CLIENT_TEXT_FIELDS: Array<{
  key: string;
  label: string;
  required?: boolean;
  helper?: string;
}> = [
  {
    key: 'client_code',
    label: 'client_code',
    required: true,
    helper: '2-10 Kleinbuchstaben (a-z), unveränderbar',
  },
  { key: 'client_name', label: 'client_name', required: true },
  { key: 'reply_to_email', label: 'reply_to_email', required: true },
  {
    key: 'mailchimp_client',
    label: 'mailchimp_client',
    helper: 'mc_api_credentials.client (z. B. tt)',
  },
  {
    key: 'allowed_customers',
    label: 'allowed_customers',
    helper: 'JSON-Array — nur für SAM-Clients relevant',
  },
  { key: 'sam_cooldown_days', label: 'sam_cooldown_days' },
  { key: 'product_cooldown_days', label: 'product_cooldown_days' },
];

const FOOTER_FIELDS: Array<keyof FooterRow> = [
  'company_name',
  'street_address',
  'postal_code',
  'city',
  'country',
  'vat_id',
  'phone',
  'email',
  'website_url',
  'privacy_url',
  'disclaimer_text',
  'disclaimer_url',
  'active',
];

const ExternalSamConfigPage: React.FC = () => {
  const [clients, setClients] = useState<ClientRow[]>([]);
  const [selected, setSelected] = useState<string>('');
  const [clientDraft, setClientDraft] = useState<Partial<ClientRow>>({});
  const [footer, setFooter] = useState<Partial<FooterRow>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [addDraft, setAddDraft] = useState<Record<string, string>>({});

  const loadClients = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get('/api/adserver/external-sam-config/clients');
      setClients(data?.data ?? []);
      if (!selected && data?.data?.length) {
        setSelected(data.data[0].client_code);
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [selected]);

  const loadFooter = useCallback(async (code: string) => {
    if (!code) return;
    try {
      const { data } = await api.get(`/api/adserver/external-sam-config/footer/${code}`);
      setFooter(data?.data ?? {});
    } catch (e) {
      console.error('footer load failed', e);
      setFooter({});
    }
  }, []);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  useEffect(() => {
    if (!selected) return;
    const row = clients.find((c) => c.client_code === selected);
    if (row) setClientDraft(row);
    loadFooter(selected);
  }, [selected, clients, loadFooter]);

  const isDirty = useMemo(() => {
    const original = clients.find((c) => c.client_code === selected);
    if (!original) return false;
    return JSON.stringify(original) !== JSON.stringify(clientDraft);
  }, [clients, selected, clientDraft]);

  const saveClient = async () => {
    if (!selected) return;
    setSaving(true);
    setError(null);
    setInfo(null);
    try {
      const { data } = await api.put(
        `/api/adserver/external-sam-config/clients/${selected}`,
        clientDraft,
      );
      if (data?.success) {
        setInfo('Client-Konfiguration gespeichert.');
        loadClients();
      } else {
        setError(data?.error ?? 'Speichern fehlgeschlagen');
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const saveFooter = async () => {
    if (!selected) return;
    setSaving(true);
    setError(null);
    setInfo(null);
    try {
      const { data } = await api.put(
        `/api/adserver/external-sam-config/footer/${selected}`,
        footer,
      );
      if (data?.success) {
        setInfo('Footer gespeichert.');
        loadFooter(selected);
      } else {
        setError(data?.error ?? 'Speichern fehlgeschlagen');
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const openAddDialog = () => {
    setAddDraft({ ...NEW_CLIENT_DEFAULTS });
    setAddError(null);
    setAddOpen(true);
  };

  const createClient = async () => {
    setAddError(null);
    const code = (addDraft.client_code ?? '').trim().toLowerCase();
    if (!/^[a-z]{2,10}$/.test(code)) {
      setAddError('Client-Code: 2-10 Kleinbuchstaben (a-z).');
      return;
    }
    if (!(addDraft.client_name ?? '').trim()) {
      setAddError('client_name ist erforderlich.');
      return;
    }
    if (!(addDraft.reply_to_email ?? '').trim()) {
      setAddError('reply_to_email ist erforderlich.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        client_code: code,
        client_name: addDraft.client_name.trim(),
        reply_to_email: addDraft.reply_to_email.trim(),
        mailchimp_client: addDraft.mailchimp_client?.trim() || null,
        allowed_customers: addDraft.allowed_customers?.trim() || '[]',
        active: Number(addDraft.active ?? '1'),
        external_sam_enabled: Number(addDraft.external_sam_enabled ?? '0'),
        sam_cooldown_days: Number(addDraft.sam_cooldown_days) || 14,
        product_cooldown_days: Number(addDraft.product_cooldown_days) || 8,
      };
      const { data } = await api.post('/api/adserver/external-sam-config/clients', payload);
      if (data?.success) {
        setInfo(`Client '${code}' angelegt. Restliche Felder unten bearbeiten.`);
        setAddOpen(false);
        setSelected(code);
        await loadClients();
      } else {
        setAddError(data?.error ?? 'Anlegen fehlgeschlagen');
      }
    } catch (e) {
      setAddError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const renderField = (key: keyof ClientRow) => {
    const value = clientDraft[key] ?? '';
    if (key === 'active' || key === 'external_sam_enabled' || key === 'use_custom_footer') {
      return (
        <Select
          size="small"
          fullWidth
          value={String(value ?? 0)}
          onChange={(e) => setClientDraft({ ...clientDraft, [key]: Number(e.target.value) })}
        >
          <MenuItem value="0">0 (aus)</MenuItem>
          <MenuItem value="1">1 (an)</MenuItem>
        </Select>
      );
    }
    return (
      <TextField
        size="small"
        fullWidth
        value={value === null ? '' : String(value)}
        onChange={(e) =>
          setClientDraft({
            ...clientDraft,
            [key]: e.target.value === '' ? null : e.target.value,
          })
        }
      />
    );
  };

  const renderFooterField = (key: keyof FooterRow) => {
    const value = footer[key] ?? '';
    if (key === 'active') {
      return (
        <Select
          size="small"
          fullWidth
          value={String(value ?? 0)}
          onChange={(e) => setFooter({ ...footer, [key]: Number(e.target.value) })}
        >
          <MenuItem value="0">0</MenuItem>
          <MenuItem value="1">1</MenuItem>
        </Select>
      );
    }
    return (
      <TextField
        size="small"
        fullWidth
        multiline={key === 'disclaimer_text'}
        minRows={key === 'disclaimer_text' ? 3 : 1}
        value={value === null ? '' : String(value)}
        onChange={(e) =>
          setFooter({ ...footer, [key]: e.target.value === '' ? null : e.target.value })
        }
      />
    );
  };

  return (
    <Box m="20px">
      <Header
        title="Externe SAM Client-Konfiguration"
        subtitle="external_sam_clients + external_sam_footer_data"
      />
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {info && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo(null)}>
          {info}
        </Alert>
      )}

      {loading ? (
        <CircularProgress />
      ) : (
        <Box>
          <Box display="flex" alignItems="center" gap={2} sx={{ mb: 3 }}>
            <Typography>Client:</Typography>
            <Select
              size="small"
              value={selected}
              onChange={(e) => setSelected(e.target.value as string)}
              sx={{ minWidth: 240 }}
            >
              {clients.map((c) => (
                <MenuItem key={c.client_code} value={c.client_code}>
                  {c.client_code.toUpperCase()} — {c.client_name}
                </MenuItem>
              ))}
            </Select>
            <Button variant="outlined" onClick={openAddDialog}>
              + Neuer Client
            </Button>
          </Box>

          {selected && (
            <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }} gap={3}>
              <Paper sx={{ p: 2, backgroundColor: '#ffffff' }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  external_sam_clients
                </Typography>
                {CLIENT_FIELD_GROUPS.map((group) => (
                  <Box key={group.label} sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      {group.label}
                    </Typography>
                    <Box display="grid" gap={1.5}>
                      {group.fields.map((f) => (
                        <Box key={String(f)}>
                          <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                            {String(f)}
                          </Typography>
                          {renderField(f)}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ))}
                <Button
                  variant="contained"
                  onClick={saveClient}
                  disabled={saving || !isDirty}
                  sx={{ mt: 1 }}
                >
                  {saving ? 'speichert…' : 'Client speichern'}
                </Button>
              </Paper>

              <Paper sx={{ p: 2, backgroundColor: '#ffffff' }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  external_sam_footer_data
                </Typography>
                <Box display="grid" gap={1.5}>
                  {FOOTER_FIELDS.map((f) => (
                    <Box key={String(f)}>
                      <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                        {String(f)}
                      </Typography>
                      {renderFooterField(f)}
                    </Box>
                  ))}
                </Box>
                <Button variant="contained" onClick={saveFooter} disabled={saving} sx={{ mt: 2 }}>
                  {saving ? 'speichert…' : 'Footer speichern'}
                </Button>
              </Paper>
            </Box>
          )}
        </Box>
      )}

      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Neuen External-Client anlegen</DialogTitle>
        <DialogContent>
          {addError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {addError}
            </Alert>
          )}
          <Box display="grid" gap={1.5} sx={{ mt: 1 }}>
            {NEW_CLIENT_TEXT_FIELDS.map((f) => (
              <TextField
                key={f.key}
                size="small"
                fullWidth
                label={f.label}
                required={f.required}
                helperText={f.helper}
                value={addDraft[f.key] ?? ''}
                onChange={(e) => setAddDraft({ ...addDraft, [f.key]: e.target.value })}
              />
            ))}
            {(['active', 'external_sam_enabled'] as const).map((k) => (
              <Box key={k}>
                <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                  {k}
                </Typography>
                <Select
                  size="small"
                  fullWidth
                  value={addDraft[k] ?? '1'}
                  onChange={(e) => setAddDraft({ ...addDraft, [k]: String(e.target.value) })}
                >
                  <MenuItem value="0">0 (aus)</MenuItem>
                  <MenuItem value="1">1 (an)</MenuItem>
                </Select>
              </Box>
            ))}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            external_sam_enabled = 0 → Client erhält Direkt- &amp; Webinar-Mailings, wird aber vom
            External-SAM-Scheduler ignoriert. Tag-IDs, URL-Tracking &amp; Footer nach dem Anlegen
            unten bearbeiten.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)} disabled={saving}>
            Abbrechen
          </Button>
          <Button variant="contained" onClick={createClient} disabled={saving}>
            {saving ? 'legt an…' : 'Client anlegen'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExternalSamConfigPage;
